import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { LicenseAgreementRenderRow } from "./licenseAgreement";
import {
  LICENSE_AGREEMENT_LINK_LABEL,
  LICENSE_AGREEMENT_ROUTE,
} from "./licenseAgreement";

export const LICENSE_AGREEMENT_SITE_HOST = "islg.ru";
export const LICENSE_AGREEMENT_SITE_HREF = `https://${LICENSE_AGREEMENT_SITE_HOST}`;

export function renderLineWithSiteLinks(
  line: string,
  anchorClassName: string,
  keyPrefix: string
): ReactNode {
  if (!line.includes(LICENSE_AGREEMENT_SITE_HOST)) {
    return line;
  }
  const parts = line.split(LICENSE_AGREEMENT_SITE_HOST);
  const nodes: ReactNode[] = [];
  parts.forEach((part, i) => {
    nodes.push(part);
    if (i < parts.length - 1) {
      nodes.push(
        <a
          key={`${keyPrefix}-site-${i}`}
          href={LICENSE_AGREEMENT_SITE_HREF}
          className={anchorClassName}
          target="_blank"
          rel="noopener noreferrer"
        >
          {LICENSE_AGREEMENT_SITE_HOST}
        </a>
      );
    }
  });
  return <>{nodes}</>;
}

type LicenseAgreementFormattedRowsProps = {
  rows: LicenseAgreementRenderRow[];
  lineClass: string;
  lineClassSubpoint: string;
  linkClass: string;
};

export function LicenseAgreementFormattedRows({
  rows,
  lineClass,
  lineClassSubpoint,
  linkClass,
}: LicenseAgreementFormattedRowsProps) {
  let continuationBlockActive = false;

  return (
    <>
      {rows.map((row, rowIdx) => {
        const primaryLine = row.kind === "line" ? row.line : row.before;
        const isSubpoint = /^\d+\.\d+\./.test(primaryLine);
        const isTopPoint = /^\d+\./.test(primaryLine) && !isSubpoint;
        const hasText = primaryLine.trim().length > 0;
        const isContinuation =
          !isSubpoint && !isTopPoint && hasText && continuationBlockActive;
        const cn =
          isSubpoint || isContinuation ? lineClassSubpoint : lineClass;

        if (row.kind === "line") {
          if (isSubpoint) {
            continuationBlockActive = /:\s*$/.test(row.line.trim());
          } else if (isTopPoint) {
            continuationBlockActive = false;
          }
          const lineContent = row.line || "\u00A0";
          return (
            <span key={rowIdx} className={cn}>
              {renderLineWithSiteLinks(lineContent, linkClass, String(rowIdx))}
            </span>
          );
        }

        if (row.kind === "lineWithLinkTail") {
          if (isSubpoint) {
            continuationBlockActive = /:\s*$/.test(row.before.trim());
          } else if (isTopPoint) {
            continuationBlockActive = false;
          }
          const beforeContent = row.before || "\u00A0";
          return (
            <span key={rowIdx} className={cn}>
              {renderLineWithSiteLinks(beforeContent, linkClass, `${rowIdx}-b`)}
              <Link to={LICENSE_AGREEMENT_ROUTE} className={linkClass}>
                {LICENSE_AGREEMENT_LINK_LABEL}
              </Link>
            </span>
          );
        }

        if (isSubpoint) {
          continuationBlockActive = /:\s*$/.test(
            `${row.before}${row.after}`.trim()
          );
        } else if (isTopPoint) {
          continuationBlockActive = false;
        }
        const beforeContent = row.before || "\u00A0";
        return (
          <span key={rowIdx} className={cn}>
            {renderLineWithSiteLinks(beforeContent, linkClass, `${rowIdx}-b`)}
            <Link to={LICENSE_AGREEMENT_ROUTE} className={linkClass}>
              {LICENSE_AGREEMENT_LINK_LABEL}
            </Link>
            {renderLineWithSiteLinks(row.after, linkClass, `${rowIdx}-a`)}
          </span>
        );
      })}
    </>
  );
}
